import { useState, useEffect, useMemo } from "react";
import { colors, fonts, frames, glyphs } from "../../../../../Styles";
import { AicoConnection, CheckBox, NewSelect, PageHeader, ToggleBox, useAicoConnection } from "../../../../../Components";
import { ButtonTypes, ListData_ValueTypes, NewFieldStyles, TableStyles_Types } from "../../../../../Definitions";
import NewButton from "../../../../../Components/BaseComponents/newButton";
import { useLazyQuery, useMutation } from "@apollo/client";
import { AVAILABLE_FIRMWARE_DEVICE, AvailableFirmwareDeviceArgs, AvailableFirmwareDeviceResponse, FIRMWARE_UPDATE_CAN, FirmwareUpdateCANArgs, FirmwareUpdateCANResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/firmwareCalls";
import { LoadingSpinner } from "../../../../../Services/wrapper";
import { DeviceRegister, DeviceRegisterArgs, DeviceRegistersResponse, GET_DEVICE_REGISTERS } from "../../../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getDeviceRegistersCall";
import { GenericRow, useColumnConstructor } from "../../../../../Components/Tables/_Tables/useColumnConstructor";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../../../../../Components/Tables/reactiveTableRenderer";

interface DeviceResult extends DeviceRegister, GenericRow {
	shouldUpdate: boolean
	success: boolean | undefined
	id: number
}

const columnsData = [
	{ headerValue: "Device Register ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true, staticColumn: true },
	{ headerValue: "CPU ID", accessor: "cpu_id", type: ListData_ValueTypes.String, sort: true, staticColumn: true },
	{ headerValue: "LC ID", accessor: "fk_lan_controllers", type: ListData_ValueTypes.Number, sort: true, staticColumn: true },
	{ headerValue: "should update", accessor: "shouldUpdateJSX", type: ListData_ValueTypes.Div, sort: false },
	{ headerValue: "result", accessor: "resultJSX", type: ListData_ValueTypes.Div, sort: false, staticColumn: true },
]

export default function FirmwareIO() {
	const [firmwareFileName, setFirmwareFileName] = useState<string | null>(null);
	const [devices, setDevices] = useState<DeviceResult[]>([]);
	const [isDirect, setIsDirect] = useState<boolean>(false);
	const [offset, setOffset] = useState<number>(0);
	const [hasMore, setHasMore] = useState<boolean>(false);
	const limit = 500;

	const { state: connectionState } = useAicoConnection();

	const [fetchAvailable, { data: dataAvailable, loading: loadingAvailable, error: errorAvailable }] = useLazyQuery<AvailableFirmwareDeviceResponse, AvailableFirmwareDeviceArgs>(
		AVAILABLE_FIRMWARE_DEVICE,
		{ fetchPolicy: "network-only" }
	);

	const [updateFirmware, { data: dataUpdate, loading: loadingUpdate, error: errorUpdate }] = useMutation<FirmwareUpdateCANResponse, FirmwareUpdateCANArgs>(
		FIRMWARE_UPDATE_CAN,
		{ fetchPolicy: "network-only" }
	);

	const [fetchDevices, { data: dataDevices, loading: loadingDevices, error: errorDevices }] = useLazyQuery<DeviceRegistersResponse, DeviceRegisterArgs>(
		GET_DEVICE_REGISTERS,
		{ fetchPolicy: "network-only" }
	);

	useEffect(() => {
		if (connectionState.domain) {
			resetState();
			fetchDevices({ variables: { domain: connectionState.domain, limit: limit, offset: 0 } });
			fetchAvailable({ variables: { domain: connectionState.domain } });
		} else {
			resetState();
		}
	}, [connectionState.domain, fetchDevices]);

	useEffect(() => {
		if (connectionState.domain && dataDevices?.device.registers) {
			const newDevices = dataDevices.device.registers.map((device) => {
				return {
					...device,
					shouldUpdate: false,
					success: undefined,
					id: device.device_register_id
				}
			})
			setDevices(prev => [...prev, ...newDevices]);
			setOffset((prev) => prev += newDevices.length);
			setHasMore(newDevices.length === limit);
		}
	}, [dataDevices, connectionState.domain]);

	useEffect(() => {
		const updateResults = dataUpdate?.device.firmwareUpdateCAN;
		if (updateResults) {
			setDevices((prev) => {
				return prev.map(device => {
					const updateResult = updateResults.find(e => e.id === device.device_register_id);

					const newDevice = { ...device, success: device.shouldUpdate ? updateResult?.success || false : undefined };
					return newDevice;
				})
			})
		}
	}, [dataUpdate]);

	const allSelected = devices.every(d => d.shouldUpdate);

	const selectFirmwareFile = firmwareFileName ? { label: firmwareFileName, value: firmwareFileName, id: firmwareFileName } : undefined;

	function resetState() {
		setDevices([]);
		setFirmwareFileName(null);
		setOffset(0);
		setHasMore(false);
	}

	const columns = useColumnConstructor({
		accessorData: columnsData,
		tableStyle: TableStyles_Types.primaryTable,
		canEdit: false,
	});

	const tableData = useMemo(() =>
		devices.map(device => ({
			...device,
			shouldUpdateJSX: (
				<CheckBox
					isChecked={device.shouldUpdate}
					onChange={(checked) => {
						setDevices(prev => {
							const updated = [...prev];
							const idx = updated.findIndex(d => d.id === device.id);
							updated[idx] = { ...updated[idx], shouldUpdate: checked };
							return updated;
						});
					}}
					disabled={loadingUpdate}
				/>
			),
			resultJSX: device.success === undefined ? undefined : (
				device.success ? <glyphs.Check fillColor={colors.systemGreen} /> : <glyphs.Error fillColor={colors.systemRed} />
			)
		})),
		[devices, loadingUpdate]
	);

	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
	});

	return (
		<frames.Page_inner>
				<PageHeader topHeader='Aico Software' subHeader='Update IO Firmware' />
			<frames._page_inner_content>
				<frames._grid_topBtns_outer>
					<frames._grid_topBtns_inner style={{ position: "relative", justifyContent: "end" }}>
						<div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", left: "20px" }}><AicoConnection hideLANControllers/></div>
						<NewButton
							content={loadingAvailable ? "Fetching..." : "Fetch avail."}
							buttonType={ButtonTypes.tableButton}
							startEnhancer={glyphs.Fetch({})}
							onClick={() => {
								if (connectionState.domain) {
									fetchAvailable({ variables: { domain: connectionState.domain } });
								}
							}}
						/>
					</frames._grid_topBtns_inner>

					<div style={{ gridArea: "2/1/3/2", border: "5px solid " + colors.greyPrimary, overflow: "hidden", borderRadius: "10px", backgroundColor: colors.greyLightTertiary, display: "flex", flexDirection: "column", gap: "40px", alignItems: "center", justifyContent: "flex-start", padding: "20px" }}>

						<div style={{ display: "flex", gap: "10px", alignItems: "flex-end", justifyContent: "center", flexWrap: "wrap" }}>
							<NewSelect
								header="Update Firmware"
								placeholder="New firmware version"
								onChange={(selectedOption) => {
									if (selectedOption?.value) {
										setFirmwareFileName(selectedOption.value);
									}
								}}
								options={dataAvailable?.device.availableFirmware.map((firmwareVersion) => ({ label: firmwareVersion.fileName, value: firmwareVersion.fileName })) ?? []}
								fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
								isRequired={true}
								isSearchable={true}
								valueOverrides={{defaultValue: selectFirmwareFile}}
							/>
							<NewButton
								onClick={() => {
									if (firmwareFileName && connectionState.domain) {
										setDevices(prev => prev.map(d => ({ ...d, success: undefined })));

										const firmwareUpdateVariables: FirmwareUpdateCANArgs = {
											domain: connectionState.domain,
											fileName: firmwareFileName,
											isDirect: isDirect,
											deviceRegisterIds: devices.filter(e => e.shouldUpdate).map(e => e.device_register_id)
										};
										updateFirmware({ variables: firmwareUpdateVariables });
									}
								}}
								disabled={loadingUpdate || !firmwareFileName || !devices.some(e => e.shouldUpdate)}
								content={loadingUpdate ? "Updating..." : "Update Firmware"}
								overrides={{ baseStyles: { width: "fit-content", height: "30px", padding: "0 10px" } }}
							/>
							<div>
								<p style={{ ...fonts.header, color: colors.greyDarkPrimary, ...fonts.fontSize4, cursor: 'cursor' }}>Direct</p>
								<ToggleBox
									onChange={(isDirect) => {
										setIsDirect(isDirect);
									}}
									icon={<></>}
									isChecked={isDirect}
								/>
							</div>
						</div>

						<div style={{ width: "100%", maxWidth: "500px" }}>
							<div style={{ borderBottom: "2px solid " + colors.greyLightPrimary, textAlign: "left", ...fonts.fontSize3, ...fonts.header, color: colors.greyDarkPrimary }}>
								Available Firmware Versions
							</div>
							{loadingAvailable && <LoadingSpinner />}
							{errorAvailable && <div style={{ color: "#aa0000" }}>Error</div>}
							{!loadingAvailable && !errorAvailable ? dataAvailable?.device.availableFirmware.length
								?
								dataAvailable.device.availableFirmware.map((firmwareVersion, i) => (
									<div key={i} style={{ display: "flex", flexDirection: "column", gap: "5px", textAlign: "left", ...fonts.fontSize3, ...fonts.paragraph, color: colors.greyDarkPrimary }}>
										{firmwareVersion.fileName}
									</div>
								))
								:
								<div style={{ textAlign: "left", fontStyle: "italic", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyTertiary }}>
									No firmware available
								</div>
								:
								<></>
							}
						</div>

						{loadingUpdate && <LoadingSpinner />}
						{errorUpdate && <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.systemRed, maxWidth: "500px", textAlign: "center" }}>Error: failed to update firmware</p>}

						{loadingDevices && <LoadingSpinner />}
						{errorDevices && <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.systemRed, maxWidth: "500px", textAlign: "center" }}>Error: failed to fetch devices</p>}
						{!loadingDevices && !errorDevices && devices.length > 0 && (
							<frames._page_inner_content>
								<frames._grid_topBtns_outer>
									<frames._grid_topBtns_inner style={{ justifyContent: "end", marginTop: "10px", gap: "5px" }}>
										{hasMore &&
											<NewButton
												content={"Fetch more"}
												buttonType={ButtonTypes.tableButton}
												startEnhancer={glyphs.Fetch({})}
												onClick={() => {
													if (connectionState.domain) {
														fetchDevices({ variables: { domain: connectionState.domain, limit: limit, offset: offset } })
													}
												}}
											/>
										}
										<NewButton
											content={allSelected ? "Deselect all" : "Select all"}
											buttonType={ButtonTypes.tableButton}
											startEnhancer={glyphs.Animal({})}
											onClick={() => {
												setDevices((prev) => {
													const newDevices = [];
													for (const device of prev) {
														newDevices.push({ ...device, shouldUpdate: allSelected ? false : true });
													}
													return newDevices;
												});
											}}
										/>
									</frames._grid_topBtns_inner>

									<ReactiveTable
										table={table}
										tableType={TableStyles_Types.primaryTable}
										emptyText="No data found"
										renderFooter={false}
									/>
								</frames._grid_topBtns_outer>
							</frames._page_inner_content>
						)}
					</div>
				</frames._grid_topBtns_outer>
			</frames._page_inner_content>
		</frames.Page_inner>
	);
}